import { createStyles, Grid } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@nutrien/cxp-components';
import { parse } from 'query-string';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import OfflineStatusDialog from '@/components/OfflineStatusDialog';
import { useOnlineStatus } from '@/utilities';
import { SYNC_COMPLETE_KEY } from '@/utilities/constants';
import { TrackingEventType, useEventTracking } from '@/utilities/hooks/useEventTracking';
import { saveSyncTimeToLocalStorage } from '@/utilities/syncHelpers';

import { dataManagerContext } from '../dataManagerContext';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      height: '100vh',
    },
    card: {
      maxWidth: '450px',
      padding: '70px 40px',
      borderRadius: '10px',
    },
    imageContainer: {
      marginBottom: '50px',
      textAlign: 'center',
      position: 'relative',
    },
    icon: {
      '& svg': {
        marginTop: '5px',
      },
    },
    progressBar: {
      width: '188px',
      position: 'absolute',
      top: '84px',
      left: 'calc(50% - 95px)',
      '& > div': {
        backgroundColor: '#fff',
        height: '2px',
      },
    },
    buttonContainer: {
      width: '200px',
      position: 'absolute',
      right: '16px',
    },
  }),
);

const Loading: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const { initialLoad } = parse(location.search);
  const isOnline = useOnlineStatus();
  const { trackEvent } = useEventTracking();

  const [progress, setProgress] = useState<number>(0);

  const [showLogout, setShowLogout] = useState(false);
  const [logoutInProgress, setLogoutInProgress] = useState(false);

  const logoutUser = async () => {
    setLogoutInProgress(true);
    await trackEvent(TrackingEventType.LOGOUT_CLICKED, {
      location: 'LoadingScreen',
    });
    history.push('/logout');
    setLogoutInProgress(false);
  };

  // Show logout button if more than 30 seconds have passed
  useEffect(() => {
    setTimeout(() => {
      setShowLogout(true);
    }, 30000);
  }, []);

  const { state } = useContext(dataManagerContext);
  const replicationStates = useMemo(() => {
    return state.replication?.replicationStates;
  }, [state]);

  useEffect(() => {
    let isMounted = true;
    if (initialLoad || !replicationStates || replicationStates.length === 0) {
      history.push('/signatures');
      return;
    }
    setProgress(0);

    let replicated = 0;
    const promises = replicationStates.map(async replicationState => {
      try {
        await replicationState.awaitInitialReplication();
      } catch (error) {
        // We don't care that much about these errors.
        // We just need to ensure that we update the `replicated` count.
        // We'll debug them just so that we know they exist.
        // eslint-disable-next-line no-console
        console.debug('Error updating loading indicator', error, replicated);
      }
      replicated += 1;
      const percent = Math.round((replicated / replicationStates.length) * 100);
      if (isMounted) setProgress(percent);
    });

    Promise.allSettled(promises).finally(() => {
      if (isMounted) {
        saveSyncTimeToLocalStorage();
        window.postMessage(SYNC_COMPLETE_KEY, '*');
        history.push('/signatures');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [replicationStates, history, initialLoad]);

  return (
    <>
      {showLogout && (
        <div className={classes.buttonContainer}>
          <Button
            color="primary"
            variant="outlined"
            onClick={logoutUser}
            id="logout-user"
            isLoading={logoutInProgress}
          >
            Logout
          </Button>
        </div>
      )}
      <Grid container className={classes.root} justify="center" alignItems="center">
        <Grid item xs={8} md={6} className={classes.card}>
          <div className={classes.imageContainer}>
            <img src="../../minesight_borer_noline.svg" alt="MineSight Borer" />
            <div className={classes.progressBar}>
              {isOnline && <LinearProgress variant="determinate" value={progress} />}
            </div>
          </div>
        </Grid>
      </Grid>
      <OfflineStatusDialog open={!!(!isOnline && initialLoad)} />
    </>
  );
};

export default Loading;
